import { trackProductEvent } from '../lib/product-analytics';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompanyJobListResult,
  CompanyJobSummary,
  CreateCompanyJobInput,
  GenerateCompanyJobDraftInput,
  JobDistribution,
} from '@tabbio/types';
import type {
  CompanyJobWithV2,
  CreateEmployerJobInput,
  GenerateCompanyJobDraftResultWithV2,
  UpdateEmployerJobInput,
} from '../lib/employer-jobs-contract';
import {
  archiveEmployerJob,
  closeCompanyJob,
  createCompanyJob,
  createEmployerJob,
  fetchCompanyJobs,
  generateCompanyJobDraft,
  offlineEmployerJob,
  publishEmployerJob,
  reopenEmployerJob,
  updateEmployerJob,
} from '../lib/jobs-api';
import { QUERY_KEYS } from '../lib/query-keys';
import { useWorkspace } from '../providers/useWorkspace';

export function useCompanyJobs(
  companyIdOverride?: string | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { companyId: activeCompanyId } = useWorkspace();
  const companyId = companyIdOverride ?? activeCompanyId;
  const enabled = Boolean(companyId) && (options?.enabled ?? true);
  const limit = options?.limit ?? 50;

  const query = useQuery<CompanyJobListResult>({
    queryKey: QUERY_KEYS.COMPANY_JOBS(companyId, limit),
    queryFn: () => fetchCompanyJobs(companyId!, limit),
    enabled,
    staleTime: 30_000,
  });

  return {
    jobs: query.data?.jobs ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

/**
 * Every employer job mutation invalidates the same surfaces: the company job
 * list (which also feeds the Careers/Open Roles block), the public catalog
 * lanes, and the job's own detail caches. Lifecycle and distribution changes
 * move jobs between all of them.
 */
function useInvalidateEmployerJob() {
  const queryClient = useQueryClient();

  return (job: Pick<CompanyJobSummary, 'id' | 'companyId'>) => {
    queryClient.invalidateQueries({ queryKey: ['jobs', 'company', job.companyId] });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOB_DETAIL(job.id) });
    queryClient.invalidateQueries({ queryKey: ['job-catalog', 'discover'] });
    queryClient.invalidateQueries({ queryKey: ['job-catalog', 'explore'] });
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(`native:${job.id}`),
    });
  };
}

/** Legacy 3-step create (kept for compatibility while old clients ship). */
export function useCreateCompanyJob() {
  const queryClient = useQueryClient();

  return useMutation<CompanyJobSummary, Error, CreateCompanyJobInput>({
    mutationFn: createCompanyJob,
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'company', job.companyId] });
    },
  });
}

/** v2 four-step create — draft or live (PRD §7). */
export function useCreateEmployerJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<CompanyJobWithV2, Error, CreateEmployerJobInput>({
    mutationFn: createEmployerJob,
    onSuccess: invalidate,
  });
}

export function useUpdateCompanyJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<
    CompanyJobWithV2,
    Error,
    { jobId: string; input: UpdateEmployerJobInput }
  >({
    mutationFn: ({ jobId, input }) => updateEmployerJob(jobId, input),
    onSuccess: (job, variables) => {
      trackProductEvent('job_updated', {
        fields_changed_count: Object.keys(variables.input ?? {}).length,
      });
      invalidate(job);
    },
  });
}

export function usePublishCompanyJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<CompanyJobWithV2, Error, string>({
    mutationFn: publishEmployerJob,
    onSuccess: (job) => {
      trackProductEvent('job_lifecycle_changed', { action: 'publish' });
      invalidate(job);
    },
  });
}

/** Take a live job offline (status → closed). Applications are preserved. */
export function useOfflineCompanyJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<CompanyJobWithV2, Error, string>({
    mutationFn: offlineEmployerJob,
    onSuccess: (job) => {
      trackProductEvent('job_lifecycle_changed', { action: 'offline' });
      invalidate(job);
    },
  });
}

export function useReopenCompanyJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<
    CompanyJobWithV2,
    Error,
    { jobId: string; distribution?: JobDistribution }
  >({
    mutationFn: ({ jobId, distribution }) =>
      reopenEmployerJob(jobId, distribution),
    onSuccess: (job) => {
      trackProductEvent('job_lifecycle_changed', { action: 'reopen' });
      invalidate(job);
    },
  });
}

export function useArchiveCompanyJob() {
  const invalidate = useInvalidateEmployerJob();

  return useMutation<CompanyJobWithV2, Error, string>({
    mutationFn: archiveEmployerJob,
    onSuccess: (job) => {
      trackProductEvent('job_lifecycle_changed', { action: 'archive' });
      invalidate(job);
    },
  });
}

export function useCloseCompanyJob() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateEmployerJob();

  return useMutation<CompanyJobSummary, Error, string>({
    mutationFn: closeCompanyJob,
    onSuccess: (job) => {
      invalidate(job);
      queryClient.setQueryData<CompanyJobSummary>(
        QUERY_KEYS.JOB_DETAIL(job.id),
        job,
      );
    },
  });
}

export function useGenerateCompanyJobDraft() {
  return useMutation<
    GenerateCompanyJobDraftResultWithV2,
    Error,
    GenerateCompanyJobDraftInput
  >({
    mutationFn: generateCompanyJobDraft,
    onSuccess: () => trackProductEvent('job_draft_generated', {}),
  });
}
